/**
 * Tier-based feature access control.
 *
 * Tier hierarchy: FREE < STARTER < PROFESSIONAL < ENTERPRISE
 *
 * Feature gating (aligned with plan feature lists):
 * - FREE:         Analytics (basic), Content, Settings
 * - STARTER+:     Revenue tracking, Brand deal management, Video Analytics
 * - PROFESSIONAL+: Health Monitor (AI anomaly detection)
 * - ENTERPRISE:   Everything in Professional
 */

const TIER_ORDER = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

/**
 * Returns the numeric index of a tier (0 = FREE, 3 = ENTERPRISE).
 */
export const tierIndex = (tier) => {
  const idx = TIER_ORDER.indexOf(tier);
  return idx >= 0 ? idx : 0;
};

/**
 * Checks if `userTier` meets or exceeds `requiredTier`.
 */
export const hasTierAccess = (userTier, requiredTier) => {
  return tierIndex(userTier || 'FREE') >= tierIndex(requiredTier);
};

/**
 * Feature → minimum tier mapping.
 * Keys match route paths (without /dashboard/ prefix).
 */
export const FEATURE_TIERS = {
  analytics:       'FREE',
  content:         'FREE',
  settings:        'FREE',
  'video-analytics': 'STARTER',
  revenue:         'STARTER',
  deals:           'STARTER',
  health:          'PROFESSIONAL',
  editor:          'FREE',
  // AI title/description generator. Documented here as a paid feature; the live runtime gate is
  // the ai_metadata_generator feature flag (min_tier=STARTER) read via useFeature, not this map.
  ai:              'STARTER',
};

/**
 * Given a userTier, returns true if the route/feature is accessible.
 * Admin users bypass all tier restrictions.
 */
export const canAccessFeature = (userTier, featureKey, isAdmin = false) => {
  if (isAdmin) return true;
  const required = FEATURE_TIERS[featureKey];
  if (!required) return true; // No restriction defined → allow
  return hasTierAccess(userTier, required);
};

/**
 * Returns a human-readable label for the minimum tier needed.
 */
export const requiredTierLabel = (featureKey) => {
  const tier = FEATURE_TIERS[featureKey];
  if (!tier || tier === 'FREE') return null;
  return tier.charAt(0) + tier.slice(1).toLowerCase();
};
